import {
  applySupplierContactRemote,
  type RemoteSupplierContact,
} from '../api/client.js';
import type { LocalReceipt } from '../db/receiptsDb.js';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function failedSupplierContact(
  name: string,
  error: string,
): RemoteSupplierContact {
  return {
    status: 'failed',
    contact_id: null,
    name,
    matches: [],
    error,
    payload_contact: null,
  };
}

export function supplierContactSectionHtml(
  item: LocalReceipt,
  preview: RemoteSupplierContact | null,
): string {
  if (item.status !== 'confirmed' && item.status !== 'sent_to_accounts') {
    return '';
  }
  if (!preview) return '';
  const locked = item.status === 'sent_to_accounts';
  const name = escapeHtml(preview.name || item.supplier_name || 'this supplier');

  if (preview.status === 'mapped') {
    return `<section class="receipt-supplier-contact" data-supplier-contact>
      <h2>Xero supplier</h2>
      <p>Linked to ${name}. Next receipt from this supplier reuses this contact.</p>
    </section>`;
  }

  if (preview.status === 'approved_create') {
    return `<section class="receipt-supplier-contact" data-supplier-contact>
      <h2>Xero supplier</h2>
      <p>You’ll create “${name}” in Xero when this receipt is sent. Nothing has been created yet.</p>
    </section>`;
  }

  if (preview.status === 'would_create') {
    return `<section class="receipt-supplier-contact" data-supplier-contact>
      <h2>Xero supplier</h2>
      <p>No Xero contact named “${name}”. Sending the receipt will create one with that name — only after you approve.</p>
      ${
        locked
          ? ''
          : `<button type="button" class="submit" data-contact-approve>Approve create</button>`
      }
    </section>`;
  }

  if (preview.status === 'needs_confirm') {
    const matches = preview.matches
      .map(
        (m) => `<li>
          <span>${escapeHtml(m.Name)}</span>
          ${
            locked
              ? ''
              : `<button type="button" class="btn-secondary" data-contact-link="${escapeHtml(m.ContactID)}">Link</button>`
          }
        </li>`,
      )
      .join('');
    return `<section class="receipt-supplier-contact" data-supplier-contact>
      <h2>Xero supplier</h2>
      <p>${escapeHtml(preview.error ?? 'A Xero contact already uses this supplier name. Confirm the link — not auto-linked.')}</p>
      <ul class="receipt-contact-matches">${matches}</ul>
      ${
        locked
          ? ''
          : `<button type="button" class="btn-secondary btn-block" data-contact-reject>Not this contact</button>`
      }
    </section>`;
  }

  return `<section class="receipt-supplier-contact" data-supplier-contact>
    <h2>Xero supplier</h2>
    <p>${escapeHtml(preview.error ?? 'Could not load Xero contacts.')}</p>
    ${
      locked
        ? ''
        : `<button type="button" class="btn-secondary btn-block" data-contact-retry>Retry</button>`
    }
  </section>`;
}

export function bindSupplierContactSection(
  root: HTMLElement,
  opts: {
    item: LocalReceipt;
    onChange: () => Promise<void>;
    onError: (text: string) => void;
  },
): void {
  const { item } = opts;
  if (item.status !== 'confirmed') return;

  root.querySelector('[data-contact-retry]')?.addEventListener('click', () => {
    void opts.onChange();
  });

  root.querySelectorAll('[data-contact-link]').forEach((el) => {
    el.addEventListener('click', () => {
      const contactId = (el as HTMLElement).dataset.contactLink;
      if (!contactId) return;
      void (async () => {
        try {
          await applySupplierContactRemote(item.id, {
            action: 'link',
            contact_id: contactId,
          });
          await opts.onChange();
        } catch (err) {
          opts.onError(
            err instanceof Error
              ? err.message
              : 'Could not link this Xero contact.',
          );
        }
      })();
    });
  });

  root.querySelector('[data-contact-reject]')?.addEventListener('click', () => {
    void (async () => {
      try {
        await applySupplierContactRemote(item.id, { action: 'reject' });
        await opts.onChange();
      } catch (err) {
        opts.onError(
          err instanceof Error
            ? err.message
            : 'Could not reject this Xero match.',
        );
      }
    })();
  });

  root.querySelector('[data-contact-approve]')?.addEventListener('click', () => {
    void (async () => {
      try {
        await applySupplierContactRemote(item.id, { action: 'approve_create' });
        await opts.onChange();
      } catch (err) {
        opts.onError(
          err instanceof Error
            ? err.message
            : 'Could not approve creating this supplier.',
        );
      }
    })();
  });
}
